// Staff and volunteer records (spec §14; ADR 0016). Being staff or a
// volunteer is a relationship on a Person, not a separate kind of person.
//
// HR and personnel records, compensation and payroll are a separate,
// restricted boundary. Hub never stores pay rates, Social Security numbers,
// tax forms, disciplinary records or medical information here, and a
// background check is a clear/not-clear flag and a date, never the report.
// Board members are not here either: they are governance's (ADR 0028).
//
// Shifts are in shifts.ts (ADR 0017): Hub owns them, and a checked-out
// volunteer shift adds its hours entry here, waiting for approval.

import { z } from 'zod'

const DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
const ID = z.string().min(1).max(100)
const text = (max: number) => z.string().trim().max(max)
const optionalText = (max: number) => text(max).nullable().optional()

export const WORKFORCE_BOUNDARY_NOTE =
  'Never put pay rates, Social Security numbers, tax forms, disciplinary notes or medical information here.'
export const BACKGROUND_CHECK_NOTE = 'Record only that the check cleared and when. Never upload or type the report.'
export const SHIFTS_NOT_BUILT_NOTE = 'Shift hours are added automatically, waiting for approval.'

export const WORKER_TYPES = ['staff', 'volunteer', 'contractor', 'intern'] as const
export type WorkerType = (typeof WORKER_TYPES)[number]
export const WORKER_TYPE_LABELS: Record<WorkerType, string> = { staff: 'Staff', volunteer: 'Volunteer', contractor: 'Contractor', intern: 'Intern' }

export const WORKFORCE_STATUSES = ['applicant', 'active', 'inactive', 'alumni'] as const
export type WorkforceStatus = (typeof WORKFORCE_STATUSES)[number]
export const WORKFORCE_STATUS_LABELS: Record<WorkforceStatus, string> = { applicant: 'Applicant', active: 'Active', inactive: 'Inactive', alumni: 'Alumni' }

export const PREFERRED_CONTACTS = ['email', 'phone', 'text'] as const
export type PreferredContact = (typeof PREFERRED_CONTACTS)[number]
export const PREFERRED_CONTACT_LABELS: Record<PreferredContact, string> = { email: 'Email', phone: 'Phone call', text: 'Text message' }

export const TSHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'] as const
export type TshirtSize = (typeof TSHIRT_SIZES)[number]

export const APPLICATION_STATUSES = ['new', 'accepted', 'declined', 'waitlisted'] as const
export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number]
export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = { new: 'New', accepted: 'Accepted', declined: 'Declined', waitlisted: 'Waitlisted' }

export const HOURS_STATUSES = ['pending', 'approved', 'rejected'] as const
export type HoursStatus = (typeof HOURS_STATUSES)[number]
export const HOURS_STATUS_LABELS: Record<HoursStatus, string> = { pending: 'Waiting for approval', approved: 'Approved', rejected: 'Rejected' }

// A qualification runs out within this many days: "expiring".
export const QUALIFICATION_EXPIRING_DAYS = 30

export function minutesLabel(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h === 0 ? `${m} min` : m === 0 ? `${h} h` : `${h} h ${m} min`
}

// Volunteer time in hours, the way people say it: "12.5 hours", "1 hour",
// "0.75 hours". Hours are logged in whole minutes; this rounds to at most
// two decimals.
export function hoursLabel(minutes: number): string {
  const hours = Math.round((minutes / 60) * 100) / 100
  return `${hours.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${hours === 1 ? 'hour' : 'hours'}`
}

// The volunteer-time line on a person's workforce card, or null to leave it
// off: someone who isn't a volunteer and has no hours this year (approved
// or waiting) has nothing to say here.
export function volunteerTimeLine(p: { workerType: WorkerType; hoursThisYear: number; hoursPending: number }): { main: string; pending: string | null } | null {
  if (p.workerType !== 'volunteer' && p.hoursThisYear === 0 && p.hoursPending === 0) return null
  return {
    main: p.hoursThisYear === 0 ? 'No volunteer hours this year' : `${hoursLabel(p.hoursThisYear)} volunteered this year`,
    pending: p.hoursPending > 0 ? `${hoursLabel(p.hoursPending)} waiting for approval` : null,
  }
}

// ------------------------------------------------------------------ shapes

export type Department = {
  id: string
  name: string
  description: string | null
  leadPersonId: string | null
  leadName: string | null
  checklistTemplateId: string | null
  checklistName: string | null
  memberCount: number
  retiredAt: string | null
  version: number
}

// Who a qualification type applies to (ADR 0061): staff means staff,
// contractors and interns; volunteers means volunteers and anyone whose
// volunteer application was accepted.
export const QUALIFICATION_APPLIES_TO = ['everyone', 'staff', 'volunteers'] as const
export type QualificationAppliesTo = (typeof QUALIFICATION_APPLIES_TO)[number]
export const QUALIFICATION_APPLIES_TO_LABELS: Record<QualificationAppliesTo, string> = { everyone: 'Staff and volunteers', staff: 'Staff only', volunteers: 'Volunteers only' }

export type QualificationType = {
  id: string
  name: string
  description: string | null
  validityMonths: number | null
  backgroundCheck: boolean
  // ADR 0061: what staff edit.
  evidenceRequired: boolean
  appliesTo: QualificationAppliesTo
  // People may report it themselves in myBRE (never a background check).
  selfReport: boolean
  // Asks for a certificate or license number.
  asksNumber: boolean
  // Held by someone, asked for by training, a shift or a staffing
  // template, or ever submitted: retired, never deleted.
  inUse: boolean
  retiredAt: string | null
  version: number
}

// Evidence files (ADR 0061): PDFs and photos only, checked by their first
// bytes, kept as confidential documents on the person.
export const QUALIFICATION_EVIDENCE_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'] as const
export type QualificationEvidenceType = (typeof QUALIFICATION_EVIDENCE_TYPES)[number]
export const isQualificationEvidenceType = (value: string): value is QualificationEvidenceType => (QUALIFICATION_EVIDENCE_TYPES as readonly string[]).includes(value)
// Through myBRE: at most this many files per request and this size each.
export const QUALIFICATION_EVIDENCE_MAX_FILES = 3
export const QUALIFICATION_EVIDENCE_MAX_BYTES = 10 * 1024 * 1024

export type QualificationEvidence = {
  id: string
  documentId: string
  version: number
  filename: string | null
  contentType: string | null
  sizeBytes: number | null
  addedVia: 'mybre' | 'hub'
  addedByName: string | null
  addedAt: string
  // Whether this viewer may open it (documents.confidential and people.read).
  visible: boolean
}

export const QUALIFICATION_SUBMISSION_STATUSES = ['pending', 'verified', 'rejected', 'withdrawn'] as const
export type QualificationSubmissionStatus = (typeof QUALIFICATION_SUBMISSION_STATUSES)[number]
export const QUALIFICATION_SUBMISSION_STATUS_LABELS: Record<QualificationSubmissionStatus, string> = { pending: 'Waiting for verification', verified: 'Verified', rejected: 'Not accepted', withdrawn: 'Withdrawn' }

// A qualification a person reported in myBRE (or staff entered for them),
// pending until staff verify or reject it. Only a verified one is a
// qualification; a pending one never counts for a shift.
export type QualificationSubmission = {
  id: string
  personId: string
  personName: string
  typeId: string
  typeName: string
  evidenceRequired: boolean
  issuedOn: string | null
  expiresOn: string | null
  credentialNumber: string | null
  note: string | null
  status: QualificationSubmissionStatus
  submittedVia: 'mybre' | 'hub'
  submittedByName: string | null
  submittedAt: string
  decidedByName: string | null
  decidedAt: string | null
  rejectReason: string | null
  qualificationId: string | null
  evidence: QualificationEvidence[]
  version: number
}

export type TrainingItem = {
  id: string
  name: string
  description: string | null
  validityMonths: number | null
  departmentIds: string[]
  qualificationTypeIds: string[]
  retiredAt: string | null
}

export type QualificationState = 'current' | 'expiring' | 'expired' | 'no_expiry'

export type PersonQualification = {
  id: string
  personId: string
  personName: string
  typeId: string
  typeName: string
  backgroundCheck: boolean
  // Background checks: cleared (true) or not (false). Null for others.
  cleared: boolean | null
  issuedOn: string | null
  expiresOn: string | null
  state: QualificationState
  // The catalog document, when one was filed and the viewer can open it.
  documentId: string | null
  documentVisible: boolean
  verifiedByName: string | null
  verifiedOn: string | null
  note: string | null
  requirementId: string | null
  recordedAt: string
  // ADR 0061.
  credentialNumber: string | null
  submissionId: string | null
  evidence: QualificationEvidence[]
}

export type TrainingCompletion = {
  id: string
  personId: string
  personName: string
  trainingItemId: string
  trainingName: string
  completedOn: string
  validUntil: string | null
  trainerName: string | null
  note: string | null
}

export type WorkforceSummary = {
  personId: string
  displayName: string
  workerType: WorkerType
  status: WorkforceStatus
  positionTitle: string | null
  departments: { id: string; name: string }[]
  startsOn: string | null
  qualificationsExpiring: number
  qualificationsExpired: number
}

export type WorkforceProfile = WorkforceSummary & {
  endsOn: string | null
  preferredContact: PreferredContact | null
  tshirtSize: TshirtSize | null
  notes: string | null
  // Only with workforce.sensitive; absent (not null) otherwise.
  emergencyContact?: { name: string | null; phone: string | null; relationship: string | null }
  qualifications: PersonQualification[]
  // Reported in myBRE (or entered for them) and waiting for staff (ADR 0061).
  pendingSubmissions: QualificationSubmission[]
  training: TrainingCompletion[]
  // Required training this person hasn't done (or whose completion ran out),
  // from their departments and their qualifications.
  trainingGaps: { trainingItemId: string; name: string; because: string; lapsedOn: string | null }[]
  // Whole minutes, despite the names: approved this calendar year, and
  // waiting for approval. Show them with hoursLabel.
  hoursThisYear: number
  hoursPending: number
  version: number
  can: { manage: boolean; sensitive: boolean }
}

export type VolunteerOpportunity = {
  id: string
  name: string
  description: string | null
  departmentId: string | null
  departmentName: string | null
  startsOn: string | null
  endsOn: string | null
  eventId: string | null
  eventName: string | null
  needsCount: number | null
  acceptedCount: number
  status: 'open' | 'closed'
  version: number
}

export type VolunteerApplication = {
  id: string
  personId: string
  personName: string
  opportunityId: string | null
  opportunityName: string | null
  departmentId: string | null
  departmentName: string | null
  status: ApplicationStatus
  sourceSubmissionId: string | null
  note: string | null
  decidedByName: string | null
  decidedAt: string | null
  decisionNote: string | null
  createdAt: string
  version: number
}

export type VolunteerHours = {
  id: string
  personId: string
  personName: string
  workedOn: string
  minutes: number
  opportunityId: string | null
  opportunityName: string | null
  eventId: string | null
  eventName: string | null
  departmentId: string | null
  departmentName: string | null
  note: string | null
  status: HoursStatus
  approvedByName: string | null
  approvedAt: string | null
  recordedByName: string | null
  recordedAt: string
}

// ------------------------------------------------------------------ bodies

const EmergencyContact = z.object({ name: optionalText(200), phone: optionalText(40), relationship: optionalText(100) })

const ProfileFields = {
  workerType: z.enum(WORKER_TYPES),
  status: z.enum(WORKFORCE_STATUSES),
  startsOn: DAY.nullable().optional(),
  endsOn: DAY.nullable().optional(),
  positionTitle: optionalText(200),
  preferredContact: z.enum(PREFERRED_CONTACTS).nullable().optional(),
  tshirtSize: z.enum(TSHIRT_SIZES).nullable().optional(),
  notes: optionalText(4000),
  emergencyContact: EmergencyContact.nullable().optional(),
}

export const WorkforceProfileBody = z.object(ProfileFields)
export const WorkforceProfilePatch = z.object({
  ...ProfileFields,
  workerType: ProfileFields.workerType.optional(),
  status: ProfileFields.status.optional(),
  expectedVersion: z.number().int().min(1),
})

export const DepartmentBody = z.object({
  name: text(120).min(1),
  description: optionalText(2000),
  leadPersonId: ID.nullable().optional(),
  // A requirements checklist (ADR 0007) stamped on a volunteer when their
  // application to this department is accepted.
  checklistTemplateId: ID.nullable().optional(),
})
export const DepartmentPatch = DepartmentBody.partial().extend({ retired: z.boolean().optional(), expectedVersion: z.number().int().min(1) })

export const DepartmentMembershipBody = z.object({ departmentId: ID })

export const QualificationTypeBody = z.object({
  name: text(120).min(1),
  description: optionalText(1000),
  validityMonths: z.number().int().min(1).max(120).nullable().optional(),
  evidenceRequired: z.boolean().optional(),
  appliesTo: z.enum(QUALIFICATION_APPLIES_TO).optional(),
  selfReport: z.boolean().optional(),
  asksNumber: z.boolean().optional(),
})
export const QualificationTypePatch = QualificationTypeBody.partial().extend({ retired: z.boolean().optional(), expectedVersion: z.number().int().min(1) })

export const QualificationBody = z.object({
  typeId: ID,
  issuedOn: DAY.nullable().optional(),
  // Left out: issued date plus the type's validity, if it has one.
  expiresOn: DAY.nullable().optional(),
  // Background checks only.
  cleared: z.boolean().optional(),
  documentId: ID.nullable().optional(),
  documentVersion: z.number().int().min(1).nullable().optional(),
  verifiedByPersonId: ID.nullable().optional(),
  verifiedOn: DAY.nullable().optional(),
  note: optionalText(1000),
  // A certificate or license number (not for a background check).
  credentialNumber: optionalText(80),
})
export const RetireBody = z.object({ reason: text(500).min(3) })

// Staff deciding a submission (ADR 0061). Verifying may correct the dates
// or the number; what the person sent stays on the submission.
export const SubmissionVerifyBody = z
  .object({
    expectedVersion: z.number().int().min(1),
    issuedOn: DAY.nullable().optional(),
    expiresOn: DAY.nullable().optional(),
    credentialNumber: optionalText(80),
    note: optionalText(1000),
  })
  .refine((b) => !b.issuedOn || !b.expiresOn || b.expiresOn >= b.issuedOn, { message: 'It must expire on or after it was issued', path: ['expiresOn'] })
export const SubmissionRejectBody = z.object({ expectedVersion: z.number().int().min(1), reason: text(500).min(3) })

// A file the web Worker has already checked and stored (never sent by a
// browser: no browser-facing route forwards it).
export const StoredEvidenceFile = z.object({
  r2Key: z.string().regex(/^docs\/[0-9a-f-]{36}$/),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  contentType: z.enum(QUALIFICATION_EVIDENCE_TYPES),
  sizeBytes: z.number().int().min(1).max(25 * 1024 * 1024),
  filename: z.string().max(255).nullable().optional(),
})
export type StoredEvidenceFile = z.infer<typeof StoredEvidenceFile>
export const RecordWithFilesBody = z.object({ qualification: QualificationBody, files: z.array(StoredEvidenceFile).min(1).max(QUALIFICATION_EVIDENCE_MAX_FILES) })
export const EvidenceFilesBody = z.object({ files: z.array(StoredEvidenceFile).min(1).max(QUALIFICATION_EVIDENCE_MAX_FILES) })

export const TrainingItemBody = z.object({
  name: text(200).min(1),
  description: optionalText(2000),
  validityMonths: z.number().int().min(1).max(120).nullable().optional(),
  departmentIds: z.array(ID).max(50).default([]),
  qualificationTypeIds: z.array(ID).max(50).default([]),
})
export const TrainingItemPatch = TrainingItemBody.partial().extend({ retired: z.boolean().optional() })

export const TrainingCompletionBody = z
  .object({
    trainingItemId: ID,
    completedOn: DAY,
    trainerPersonId: ID.nullable().optional(),
    trainerName: optionalText(200),
    note: optionalText(1000),
  })
  .refine((b) => !(b.trainerPersonId && b.trainerName), { message: 'Choose a trainer or type a name, not both', path: ['trainerName'] })

export const OpportunityBody = z
  .object({
    name: text(200).min(1),
    description: optionalText(4000),
    departmentId: ID.nullable().optional(),
    startsOn: DAY.nullable().optional(),
    endsOn: DAY.nullable().optional(),
    eventId: ID.nullable().optional(),
    needsCount: z.number().int().min(1).max(10_000).nullable().optional(),
  })
  .refine((b) => !b.startsOn || !b.endsOn || b.endsOn >= b.startsOn, { message: 'It must end on or after it starts', path: ['endsOn'] })
export const OpportunityPatch = z.object({
  name: text(200).min(1).optional(),
  description: optionalText(4000),
  departmentId: ID.nullable().optional(),
  startsOn: DAY.nullable().optional(),
  endsOn: DAY.nullable().optional(),
  eventId: ID.nullable().optional(),
  needsCount: z.number().int().min(1).max(10_000).nullable().optional(),
  status: z.enum(['open', 'closed']).optional(),
  expectedVersion: z.number().int().min(1),
})

export const ApplicationBody = z.object({ personId: ID, opportunityId: ID.nullable().optional(), departmentId: ID.nullable().optional(), note: optionalText(2000) })
export const ApplicationDecisionBody = z.object({
  status: z.enum(['accepted', 'declined', 'waitlisted']),
  note: optionalText(1000),
  expectedVersion: z.number().int().min(1),
})

export const HoursBody = z.object({
  personId: ID,
  workedOn: DAY,
  minutes: z.number().int().min(1).max(24 * 60),
  opportunityId: ID.nullable().optional(),
  eventId: ID.nullable().optional(),
  departmentId: ID.nullable().optional(),
  note: optionalText(1000),
  // Approve as it is logged.
  approve: z.boolean().optional(),
})
export const HoursDecisionBody = z.object({ status: z.enum(['approved', 'rejected']), note: optionalText(500) })
